import { t } from "i18next";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { darkModeTextLinkSx, visitedLinkSx } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";

import { useAuth } from "@/src/auth/hooks/useAuth";
import type { FAQItem } from "@/src/types/faq/faq.types";
import { getConsumerGtmContext } from "@/src/utils/gtmConsumerContext";
import { toSxArray } from "@/src/utils/sx";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";

interface FAQSingleAccordionProps {
  item: FAQItem;
  index?: number;
  detailsTextSx?: SxProps<Theme>;
}

const FAQSingleAccordion = (props: FAQSingleAccordionProps) => {
  const { item, index, detailsTextSx } = props;
  const { title, content, links, listCarItems, listMotoItems, id } = item;
  const sendDataToGTM = useGTMDispatch();
  const auth = useAuth();

  let processedContent = "";
  if (links && links.length > 0) {
    const vars = {};
    links.forEach((link, index) => {
      const propName = `link${index + 1}`;
      const linkText = t(`faq.${link.text}`);
      vars[propName] = `
        <a href="${link.url}" target="_blank" rel="noreferrer">${linkText}</a>`;
      vars["interpolation"] = { escapeValue: false };
    });
    processedContent = t(`faq.${content}`, vars);
  } else {
    processedContent = t(`faq.${content}`);
  }

  return (
    // @ts-ignore
    <Accordion
      sx={{
        "&:before": {
          height: "0px"
        },
        "&.MuiAccordion-root": {
          boxShadow: "none",
          marginBottom: 0,
          "&:first-of-type": {
            borderTop: "1px solid",
            borderColor: "divider"
          }
        },
        ".MuiAccordionSummary-gutters": {
          boxShadow: "none"
        },
        ".Mui-expanded": {
          boxShadow: "none"
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        onClick={() => {
          sendDataToGTM({
            event: "faq_item_clicked",
            faq_item: t(`faq.${title}`),
            ...getConsumerGtmContext(auth.isAuthenticated)
          });
        }}
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{ pl: 1 }}
      >
        <Box
          sx={{
            width: "2rem"
          }}
        >
          <Typography variant="body1">{index}</Typography>
        </Box>
        <Box
          sx={{
            width: "calc(100% - 2rem)"
          }}
        >
          <Typography>{t(`faq.${title}`)}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={[
          {
            pl: 5,
            pr: 6,
            pb: 4,
            fontWeight: "fontWeightRegular",
            color: "text.secondary"
          },
          ...toSxArray(detailsTextSx)
        ]}
      >
        <Typography
          variant="body1"
          dangerouslySetInnerHTML={{ __html: processedContent }}
          sx={[
            schemeSx((muiTheme) => ({
              color: muiTheme.palette.text.secondary,
              ">a": {
                color: muiTheme.palette.semantic.secondary.main,
                "&:focus, &:active": {
                  color: muiTheme.palette.semantic.secondary.main
                },
                ...visitedLinkSx(muiTheme),
                ...darkModeTextLinkSx(muiTheme)
              }
            })),
            ...toSxArray(detailsTextSx)
          ]}
        />
      </AccordionDetails>
      {id === 16 && (
        <>
          <Box>
            <Typography sx={{ ml: 5, mb: 2 }}>
              {t("consumer.wizard.stepMedia.bulletListTitle")}
            </Typography>
            <Box sx={{ ml: 8, mb: 2 }}>
              {listCarItems && (
                <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                  {listCarItems.map((listItem) => (
                    <li key={`list-item-${listItem}`}>
                      <Typography
                        variant="body1"
                        sx={[
                          { color: "text.secondary" },
                          ...toSxArray(detailsTextSx)
                        ]}
                      >
                        {listItem}
                      </Typography>
                    </li>
                  ))}
                </ul>
              )}
            </Box>
          </Box>
        </>
      )}
      {id === 16 && (
        <>
          <Box>
            <Typography sx={{ ml: 5, mb: 2 }}>
              {t("consumer.wizard.moto.stepMedia.bulletListTitle")}
            </Typography>
            <Box sx={{ ml: 8, mb: 2 }}>
              {listMotoItems && (
                <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                  {listMotoItems.map((listItem) => (
                    <li key={`list-item-${listItem}`}>
                      <Typography
                        variant="body1"
                        sx={[
                          { color: "text.secondary" },
                          ...toSxArray(detailsTextSx)
                        ]}
                      >
                        {listItem}
                      </Typography>
                    </li>
                  ))}
                </ul>
              )}
            </Box>
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={[
                {
                  color: "text.secondary",
                  ml: 5
                },
                ...toSxArray(detailsTextSx)
              ]}
            >
              {t("consumer.wizard.stepMedia.helpText")}
            </Typography>
          </Box>
        </>
      )}
    </Accordion>
  );
};

export default FAQSingleAccordion;
